import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import DashboardLayout from "@/components/DashboardLayout";
import PortalLayout from "@/components/PortalLayout";
import PartnerLayout from "@/components/PartnerLayout";
import { ForcePasswordChange } from "@/components/ForcePasswordChange";

import Landing from "@/pages/Landing";
import ForClients from "@/pages/ForClients";
import ForCandidates from "@/pages/ForCandidates";
import OurStory from "@/pages/OurStory";
import AdvisoryServices from "@/pages/AdvisoryServices";
import FractionalVCPlatform from "@/pages/FractionalVCPlatform";
import ForRecruiters from "@/pages/ForRecruiters";
import FeaturedJobs from "@/pages/FeaturedJobs";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import PortalRedirect from "@/pages/PortalRedirect";

import Dashboard from "@/pages/Dashboard";
import Organizations from "@/pages/Organizations";
import OrganizationDetail from "@/pages/OrganizationDetail";
import Candidates from "@/pages/Candidates";
import CandidateDetail from "@/pages/CandidateDetail";
import Searches from "@/pages/Searches";
import SearchDetail from "@/pages/SearchDetail";
import AIWorkspace from "@/pages/AIWorkspace";
import Settings from "@/pages/Settings";
import AdminJobs from "@/pages/AdminJobs";
import AdminBilling from "@/pages/AdminBilling";
import AdminContracts from "@/pages/AdminContracts";
import AdminCommunications from "@/pages/AdminCommunications";
import AdminLeads from "@/pages/AdminLeads";
import AdminRecruiterWaitlist from "@/pages/AdminRecruiterWaitlist";
import AdminInvites from "@/pages/AdminInvites";
import AdminUsers from "@/pages/AdminUsers";
import AdminAdvisory from "@/pages/AdminAdvisory";
import InviteAccept from "@/pages/InviteAccept";

import ClientDashboard from "@/pages/portal/ClientDashboard";
import ClientSearches from "@/pages/portal/ClientSearches";
import ClientSearchDetail from "@/pages/portal/ClientSearchDetail";
import ClientJobs from "@/pages/portal/ClientJobs";
import ClientBilling from "@/pages/portal/ClientBilling";
import ClientContracts from "@/pages/portal/ClientContracts";
import CandidateDashboard from "@/pages/portal/CandidateDashboard";
import CandidateProfile from "@/pages/portal/CandidateProfile";
import Opportunities from "@/pages/portal/Opportunities";
import JobDetail from "@/pages/portal/JobDetail";
import Applications from "@/pages/portal/Applications";
import Messages from "@/pages/portal/Messages";
import BillingReturn from "@/pages/portal/BillingReturn";

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <ForcePasswordGate>
          <Routes>
            {/* Public */}
            <Route path="/" element={<Landing />} />
            <Route path="/for-clients" element={<ForClients />} />
            <Route path="/for-candidates" element={<ForCandidates />} />
            <Route path="/our-story" element={<OurStory />} />
            <Route path="/advisory-services" element={<AdvisoryServices />} />
            <Route path="/vc-platform" element={<FractionalVCPlatform />} />
            <Route path="/for-recruiters" element={<ForRecruiters />} />
            <Route path="/featured-jobs" element={<FeaturedJobs />} />
            <Route path="/login" element={<Login />} />
            <Route path="/invite/:token" element={<InviteAccept />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/portal" element={<PortalRedirect />} />
            <Route path="/billing/return" element={<ProtectedRoute><BillingReturn /></ProtectedRoute>} />

            {/* Internal / admin */}
            <Route
              element={<ProtectedRoute roles={["internal"]}><DashboardLayout /></ProtectedRoute>}
            >
              <Route path="/app" element={<Dashboard />} />
              <Route path="/app/leads" element={<AdminLeads />} />
              <Route path="/app/advisory" element={<AdminAdvisory />} />
              <Route path="/app/recruiter-waitlist" element={<AdminRecruiterWaitlist />} />
              <Route path="/app/organizations" element={<Organizations />} />
              <Route path="/app/organizations/:id" element={<OrganizationDetail />} />
              <Route path="/app/candidates" element={<Candidates />} />
              <Route path="/app/invites" element={<AdminInvites />} />
              <Route path="/app/candidates/:id" element={<CandidateDetail />} />
              <Route path="/app/searches" element={<Searches />} />
              <Route path="/app/searches/:id" element={<SearchDetail />} />
              <Route path="/app/jobs" element={<AdminJobs />} />
              <Route path="/app/communications" element={<AdminCommunications />} />
              <Route path="/app/contracts" element={<AdminContracts />} />
              <Route path="/app/billing" element={<AdminBilling />} />
              <Route path="/app/ai" element={<AIWorkspace />} />
              <Route path="/app/users" element={<AdminUsers />} />
              <Route path="/app/settings" element={<Settings />} />
            </Route>

            {/* Client portal */}
            <Route
              element={<ProtectedRoute roles={["client"]}><PortalLayout variant="client" /></ProtectedRoute>}
            >
              <Route path="/client" element={<ClientDashboard />} />
              <Route path="/client/searches" element={<ClientSearches />} />
              <Route path="/client/searches/:id" element={<ClientSearchDetail />} />
              <Route path="/client/jobs" element={<ClientJobs />} />
              <Route path="/client/contracts" element={<ClientContracts />} />
              <Route path="/client/billing" element={<ClientBilling />} />
              <Route path="/client/messages" element={<Messages />} />
            </Route>

            {/* Recruiting Partner portal */}
            <Route
              element={<ProtectedRoute roles={["recruiter_partner"]}><PartnerLayout /></ProtectedRoute>}
            >
              <Route path="/partner" element={<ClientJobs />} />
              <Route path="/partner/billing" element={<ClientBilling />} />
            </Route>

            {/* Candidate portal */}
            <Route
              element={<ProtectedRoute roles={["candidate"]}><PortalLayout variant="candidate" /></ProtectedRoute>}
            >
              <Route path="/candidate" element={<CandidateDashboard />} />
              <Route path="/candidate/opportunities" element={<Opportunities />} />
              <Route path="/candidate/jobs/:id" element={<JobDetail />} />
              <Route path="/candidate/applications" element={<Applications />} />
              <Route path="/candidate/profile" element={<CandidateProfile />} />
              <Route path="/candidate/messages" element={<Messages />} />
            </Route>
          </Routes>
          </ForcePasswordGate>
        </BrowserRouter>
        <Toaster position="top-right" theme="light" />
      </AuthProvider>
    </div>
  );
}

function ForcePasswordGate({ children }) {
  const { user } = useAuth();
  if (user && user.must_change_password) return <ForcePasswordChange />;
  return children;
}

export default App;
